import { z } from 'zod';

/**
 * Hand-authored shared Zod for the auth FORMS (ADR-0014: business rules live in the contract package
 * so client and server validate identically). Constraints mirror the API DTOs/VOs exactly:
 *   - email: a valid address, ≤254 chars — the global login id (ADR-0071), used for login + register
 *   - password: 8–128 (register/change), 1–128 on login (login accepts any existing password)
 *   - PIN: 4–6 digits
 *   - household / member ids: UUIDv7 (validated as uuid; the API pins v7)
 *
 * Validation `message`s are i18n KEYS (resolved by the form layer via react-i18next), never English
 * strings — ADR-0025. Keys use the COLON namespace form (`validation:<key>`) so i18next resolves the
 * `validation` namespace (config: `nsSeparator: ':'`, `keySeparator: '.'`); a dot-form key like
 * `validation.required` would not resolve against the flat `validation` catalog and would render raw.
 */

const uuid = z.string().uuid('validation:uuid');
// A filled-but-malformed address gets the dedicated `validation:email` message rather than the
// generic "required" (which is wrong + unactionable on a non-empty field).
const email = z
  .string()
  .min(1, 'validation:required')
  .email('validation:email')
  .max(254, 'validation:email');

export const loginSchema = z.object({
  email,
  password: z.string().min(1, 'validation:required').max(128, 'validation:password'),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const registerSchema = z.object({
  householdName: z.string().min(1, 'validation:required').max(80),
  timezone: z.string().min(1, 'validation:required'),
  currency: z.string().length(3, 'validation:currency'),
  email,
  ownerDisplayName: z.string().min(1, 'validation:required').max(40),
  // Avatar key (the API validates it against the AvatarKeyValue set; the form picks from that set).
  ownerAvatar: z.string().min(1, 'validation:required'),
  password: z.string().min(8, 'validation:password').max(128, 'validation:password'),
  defaultLocale: z.string().min(2, 'validation:locale'),
  ownerLocale: z.string().min(2, 'validation:locale').optional(),
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const childLoginSchema = z.object({
  householdId: uuid,
  childId: uuid,
  pin: z.string().regex(/^[0-9]{4,6}$/, 'validation:pin'),
});
export type ChildLoginInput = z.infer<typeof childLoginSchema>;

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'validation:required').max(128, 'validation:password'),
  newPassword: z.string().min(8, 'validation:password').max(128, 'validation:password'),
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

/**
 * Accept-invitation form (a relative redeems their invite link and sets a first password). `password`
 * mirrors the API DTO (8–128); `confirmPassword` is a client-only typo guard for a brand-new secret
 * and is dropped before the request. The mismatch `message` is an i18n key like the rest (ADR-0025).
 */
export const acceptInvitationSchema = z
  .object({
    password: z.string().min(8, 'validation:password').max(128, 'validation:password'),
    confirmPassword: z.string().min(1, 'validation:required').max(128, 'validation:password'),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: 'validation:passwordMatch',
    path: ['confirmPassword'],
  });
export type AcceptInvitationInput = z.infer<typeof acceptInvitationSchema>;

/** Forgot-password form (request a reset link by email; ADR-0071). */
export const forgotPasswordSchema = z.object({ email });
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

/**
 * Reset-password form (set a new password from a reset link; ADR-0071). `password` mirrors the API
 * DTO (8–128); `confirmPassword` is a client-only typo guard dropped before the request.
 */
export const resetPasswordSchema = z
  .object({
    password: z.string().min(8, 'validation:password').max(128, 'validation:password'),
    confirmPassword: z.string().min(1, 'validation:required').max(128, 'validation:password'),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: 'validation:passwordMatch',
    path: ['confirmPassword'],
  });
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;
